import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useLocation } from 'react-router'
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform, useReducedMotion, type MotionValue } from 'motion/react'
import { cn } from '@/lib/utils'
import type { NavGroup, NavItem } from './sidebar-nav'

/**
 * The collapsed sidebar as a macOS-style Dock: icons swell as the pointer passes, the nearest
 * one most and its neighbours on a smooth falloff, and the rail makes room for them. The
 * hovered item's name floats beside it on a frosted pill, fading in like the Dock's labels.
 *
 * Magnification follows the pointer's height along the rail through one motion value; every
 * tile derives its size from its distance to it and eases there on a spring, so the wave is
 * continuous rather than stepped. Reduced motion keeps the labels and drops the swelling.
 */

const BASE = 38
/** Default largest size; people set their own in Sidebar settings. */
export const DEFAULT_DOCK_MAGNIFICATION = 72
/** How far (px) the swell reaches above and below the pointer, scaled with the magnification. */
const reachFor = (max: number) => 110 + (max - BASE) * 1.6

export function SidebarDock({ groups, magnification = DEFAULT_DOCK_MAGNIFICATION }: { groups: NavGroup[]; magnification?: number }) {
  const reduce = useReducedMotion()
  // Resting size that lets every item fit the window (the Dock never scrolls), at most 38px.
  const count = groups.reduce((n, g) => n + g.items.length, 0)
  const [winH, setWinH] = useState(() => (typeof window === 'undefined' ? 900 : window.innerHeight))
  useEffect(() => {
    const onResize = () => setWinH(window.innerHeight)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  const base = Math.max(26, Math.min(BASE, Math.floor((winH - 150 - groups.length * 17) / Math.max(1, count)) - 4))
  const mouseY = useMotionValue(Number.POSITIVE_INFINITY)
  const [label, setLabel] = useState<{ text: string; y: number; x: number } | null>(null)
  const hovered = useRef<{ el: HTMLElement; text: string } | null>(null)
  const frame = useRef(0)
  // Tiles move as they swell, so the label follows its tile every frame the pointer moves.
  const follow = () => {
    cancelAnimationFrame(frame.current)
    let last = ''
    let still = 0
    const tick = () => {
      const h = hovered.current
      if (!h) return
      const r = h.el.getBoundingClientRect()
      const key = `${Math.round(r.right)}:${Math.round(r.top + r.height / 2)}`
      if (key !== last) {
        last = key
        still = 0
        setLabel({ text: h.text, y: r.top + r.height / 2, x: r.right })
      } else {
        still += 1
      }
      // Keep tracking while the springs settle; stop once the tile has been still for a moment.
      if (still < 12) frame.current = requestAnimationFrame(tick)
    }
    frame.current = requestAnimationFrame(tick)
  }
  const { search } = useLocation()
  const query = new URLSearchParams(search)

  return (
    <nav
      aria-label="Navigation"
      className="flex flex-col items-start gap-1 py-2 pl-[12px]"
      onMouseMove={(e) => {
        mouseY.set(e.clientY)
        follow()
      }}
      onMouseLeave={() => {
        mouseY.set(Number.POSITIVE_INFINITY)
        hovered.current = null
        setLabel(null)
      }}
    >
      {groups.map((group, gi) => (
        <div key={group.label} className="flex flex-col items-start gap-1">
          {gi > 0 ? <span aria-hidden className="my-1.5 ml-1.5 h-px w-7 bg-nav-line" /> : null}
          {group.items.map((item) => (
            <DockItem
              key={item.to}
              item={item}
              mouseY={mouseY}
              max={Math.max(magnification, base + 6)}
              base={base}
              reduce={reduce}
              matchQuery={query}
              onHover={(el) => {
                hovered.current = { el, text: item.label }
                follow()
              }}
              onLeave={() => {
                hovered.current = null
                setLabel(null)
              }}
            />
          ))}
        </div>
      ))}
      {typeof document !== 'undefined'
        ? createPortal(
            <AnimatePresence>
              {label ? (
                <motion.div
                  key="dock-label"
                  initial={{ opacity: 0, x: -6, scale: 0.96 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -4, scale: 0.98, transition: { duration: 0.12 } }}
                  transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 520, damping: 38, mass: 0.6 }}
                  style={{ left: label.x + 13, top: label.y }}
                  className="pointer-events-none fixed z-toast -translate-y-1/2"
                >
                  {/* The macOS Dock label: an opaque graphite pill with a hairline lighter edge and the
                      system font, its pointer joined seamlessly on the side, aimed at the icon's centre. */}
                  <span className="tt-dock-label relative block whitespace-nowrap rounded-[11px] px-3.5 py-[5px] text-[15px] leading-[1.35] text-white/95">
                    <span aria-hidden className="tt-dock-label-tip absolute -left-[6px] top-1/2 size-[11px] -translate-y-1/2 rotate-45 rounded-[2px]" />
                    {label.text}
                  </span>
                </motion.div>
              ) : null}
            </AnimatePresence>,
            document.body,
          )
        : null}
    </nav>
  )
}

function DockItem({
  item,
  mouseY,
  max,
  base,
  reduce,
  matchQuery,
  onHover,
  onLeave,
}: {
  item: NavItem
  mouseY: MotionValue<number>
  max: number
  base: number
  reduce: boolean | null
  matchQuery: URLSearchParams
  onHover: (el: HTMLElement) => void
  onLeave: () => void
}) {
  const ref = useRef<HTMLAnchorElement>(null)
  // Distance from the pointer to this tile's centre -> size, on a cosine falloff like the Dock.
  const target = useTransform(mouseY, (y) => {
    const el = ref.current
    if (!el || !Number.isFinite(y) || reduce) return base
    const r = el.getBoundingClientRect()
    const d = Math.abs(y - (r.top + r.height / 2))
    const reach = reachFor(max)
    if (d > reach) return base
    return base + (max - base) * (0.5 + 0.5 * Math.cos((Math.PI * d) / reach))
  })
  const size = useSpring(target, { stiffness: 420, damping: 30, mass: 0.35 })
  const iconSize = useTransform(size, (s) => Math.round(s * 0.46))
  // Depth grows with the swell, so a big tile visibly lifts off the page beneath it.
  const shadow = useTransform(size, (s) => {
    const t = Math.max(0, Math.min(1, (s - 38) / 50))
    return `0 ${Math.round(2 + t * 12)}px ${Math.round(6 + t * 26)}px -6px color-mix(in srgb, black ${Math.round(25 + t * 35)}%, transparent)`
  })
  const Icon = item.icon

  return (
    <NavLink
      ref={ref}
      to={item.to}
      end={item.end}
      aria-label={item.label}
      onMouseEnter={(e) => onHover(e.currentTarget)}
      onFocus={(e) => onHover(e.currentTarget)}
      onMouseLeave={onLeave}
      onBlur={onLeave}
      className="group relative block outline-none"
    >
      {({ isActive: pathActive }) => {
        const active = pathActive && (!item.matchSearch || item.matchSearch(matchQuery))
        return (
          <>
            {/* The Dock's "running" dot, on the outer edge. */}
            <span aria-hidden className={cn('absolute -left-[9px] top-1/2 size-1 -translate-y-1/2 rounded-full bg-white transition-opacity', active ? 'opacity-100' : 'opacity-0')} />
            <motion.span
              style={{ width: size, height: size, boxShadow: shadow }}
              whileTap={{ scale: 0.9 }}
              className={cn(
                'grid place-items-center rounded-[28%] transition-colors duration-200 group-focus-visible:ring-2 group-focus-visible:ring-white/60',
                // Solid tiles: they swell over the white page, so nothing may be see-through.
                'border border-white/10',
                active
                  ? 'bg-brand text-white'
                  : 'bg-[color-mix(in_srgb,var(--color-nav-bg)_86%,white)] text-white/80 group-hover:bg-[color-mix(in_srgb,var(--color-nav-bg)_76%,white)] group-hover:text-white',
              )}
            >
              <motion.span style={{ width: iconSize, height: iconSize }} className="grid place-items-center">
                <Icon className="size-full" strokeWidth={1.9} />
              </motion.span>
            </motion.span>
          </>
        )
      }}
    </NavLink>
  )
}
